import type { AuthSession } from './types';

/**
 * Keeps tabs of the same browser in step:
 * - a Web Lock makes sure only one tab refreshes at a time (two tabs presenting
 *   the same refresh token would look like token theft to the API);
 * - a BroadcastChannel shares the new session and logouts with other tabs.
 * Both degrade gracefully where unsupported (older browsers, jsdom in tests).
 */
export type AuthMessage = { type: 'session'; session: AuthSession; issuedAt: number } | { type: 'logout' };

const CHANNEL = 'ecrm-auth';
const REFRESH_LOCK = 'ecrm-auth-refresh';

const channel: BroadcastChannel | null = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL);

export const broadcastAuth = (message: AuthMessage): void => {
  channel?.postMessage(message);
};

export const onAuthMessage = (handler: (message: AuthMessage) => void): (() => void) => {
  if (!channel) return () => undefined;
  const listener = (event: MessageEvent<AuthMessage>) => handler(event.data);
  channel.addEventListener('message', listener);
  return () => channel.removeEventListener('message', listener);
};

export const withRefreshLock = async <T>(task: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return task();
  return await locks.request(REFRESH_LOCK, task);
};
